import { useEffect, type ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Command, CommandInput } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { DismissibleChip } from "@/components/consultation/shared/DismissibleChip";
import { Loader2, ArrowLeft, Plus } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface ChipItem {
  id: string;
  label: string;
  status?: "provisional" | "confirmed";
}

export interface ChipGroup {
  key: string;
  label: string;
  items: ChipItem[];
  /** Items displayed as inactive (greyed, strikethrough) within this group. */
  inactiveItems?: ChipItem[];
  /** Override chip styling for this group (applied via className). */
  chipClassName?: string;
  /** Override remove-button styling for this group. */
  removeClassName?: string;
  /** Optional: enable a confirm (✓) action on each active chip in this group. */
  onConfirmItem?: (id: string) => void;
}

interface SearchableChipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;

  /* ---- Already-added items ---- */
  /** Flat list — renders a single chip row. Use `addedGroups` for grouped display. */
  addedItems?: ChipItem[];
  /** Grouped list — renders chips under labelled subsections (e.g. History / New). */
  addedGroups?: ChipGroup[];
  onRemoveItem: (id: string) => void;
  /** Optional controls rendered between selected chips and search. */
  controlsSlot?: ReactNode;

  /* ---- Search ---- */
  /** Label shown above the search input (e.g. "Add health history here"). */
  searchLabel?: string;
  searchPlaceholder?: string;
  searchResults: ChipItem[];
  searchTerm: string;
  onSearchChange: (term: string) => void;
  minSearchChars?: number;
  isSearching?: boolean;
  emptySearchMessage?: string;

  /* ---- Add behaviour ---- */
  onAddItem: (id: string) => void;
  showCustomAdd?: boolean;
  customAddDisabled?: boolean;
  customAddLabel?: string;
  onAddCustomItem?: (term: string) => void;

  /* ---- Optional form view ---- */
  formView?: ReactNode;
  showForm?: boolean;
  onBackToManage?: () => void;
  formTitle?: string;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export const SearchableChipDialog = ({
  open,
  onOpenChange,
  title,
  addedItems,
  addedGroups,
  onRemoveItem,
  controlsSlot,
  searchLabel,
  searchPlaceholder = "Search Here",
  searchResults,
  searchTerm,
  onSearchChange,
  minSearchChars = 2,
  isSearching = false,
  emptySearchMessage = "No results found",
  onAddItem,
  showCustomAdd = false,
  customAddDisabled = false,
  customAddLabel = "Add as custom",
  onAddCustomItem,
  formView,
  showForm = false,
  onBackToManage,
  formTitle = "Add Details",
}: SearchableChipDialogProps) => {
  // Reset search when dialog opens
  useEffect(() => {
    if (open) onSearchChange("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const isFormMode = formView != null && showForm;
  const trimmedSearchTerm = searchTerm.trim();
  const canShowCustomAdd =
    showCustomAdd && trimmedSearchTerm.length >= minSearchChars;

  /* ---- Derive added-items display ---- */
  const hasAddedItems =
    (addedItems && addedItems.length > 0) ||
    (addedGroups &&
      addedGroups.some(
        (g) =>
          g.items.length > 0 || (g.inactiveItems && g.inactiveItems.length > 0)
      ));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] flex flex-col p-0 gap-0 overflow-hidden border-border/60">
        {/* ---- Header ---- */}
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="z-h2 text-foreground flex items-center gap-2">
            {isFormMode && onBackToManage && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 -ml-2 mr-1"
                onClick={onBackToManage}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            {isFormMode ? formTitle : title}
          </DialogTitle>
        </DialogHeader>

        {/* Primary-tinted separator */}
        <div className="h-px bg-primary/30 shrink-0" />

        {isFormMode ? (
          /* ---- Form view ---- */
          <div className="overflow-y-auto p-6 max-h-[70vh]">{formView}</div>
        ) : (
          /* ---- Manage view ---- */
          <div className="flex flex-col">
            {/* Selected chips — flat row directly below separator */}
            {hasAddedItems && (
              <div className="px-6 pt-4 pb-3 shrink-0">
                {addedGroups ? (
                  <div className="space-y-3">
                    {addedGroups.map((group) => {
                      const hasActive = group.items.length > 0;
                      const hasInactive =
                        group.inactiveItems && group.inactiveItems.length > 0;
                      if (!hasActive && !hasInactive) return null;
                      return (
                        <div key={group.key} className="space-y-1.5">
                          <p className="z-eyebrow text-muted-foreground">
                            {group.label}
                          </p>
                          {hasActive && (
                            <div className="flex flex-wrap gap-2">
                              {group.items.map((item) => (
                                <DismissibleChip
                                  key={item.id}
                                  label={item.label}
                                  status={item.status}
                                  onConfirm={
                                    group.onConfirmItem
                                      ? () => group.onConfirmItem!(item.id)
                                      : undefined
                                  }
                                  onRemove={() => onRemoveItem(item.id)}
                                  className={
                                    group.chipClassName ??
                                    "border-primary/40 bg-primary/10 text-primary"
                                  }
                                  removeClassName={
                                    group.removeClassName ??
                                    "bg-primary/15 text-primary/60 hover:bg-destructive/20 hover:text-destructive"
                                  }
                                />
                              ))}
                            </div>
                          )}
                          {hasInactive && (
                            <div className="space-y-1 mt-1">
                              <p className="z-hint text-muted-foreground/60 italic">
                                Inactive
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {group.inactiveItems!.map((item) => (
                                  <DismissibleChip
                                    key={`inactive-${item.id}`}
                                    label={item.label}
                                    onRemove={() => onRemoveItem(item.id)}
                                    className="border-border/60 bg-muted/80 text-muted-foreground line-through decoration-muted-foreground/40"
                                    removeClassName="bg-muted text-muted-foreground/60 hover:bg-destructive/20 hover:text-destructive"
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {addedItems!.map((item) => (
                      <DismissibleChip
                        key={item.id}
                        label={item.label}
                        status={item.status}
                        onRemove={() => onRemoveItem(item.id)}
                        className="border-primary/40 bg-primary/10 text-primary"
                        removeClassName="bg-primary/15 text-primary/60 hover:bg-destructive/20 hover:text-destructive"
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {controlsSlot && (
              <div className="px-6 pt-3 pb-1 shrink-0">{controlsSlot}</div>
            )}

            {/* Search section */}
            <div className="px-6 pt-3 pb-4 space-y-2">
              {searchLabel && (
                <p className="z-secondary text-muted-foreground">
                  {searchLabel}
                </p>
              )}
              <Command
                shouldFilter={false}
                className="overflow-visible bg-transparent"
              >
                <div className="relative">
                  <CommandInput
                    placeholder={searchPlaceholder}
                    value={searchTerm}
                    onValueChange={onSearchChange}
                    autoFocus
                    className="z-body"
                  />
                  {isSearching && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
                  )}
                </div>
              </Command>

              {/* Search results — only rendered when there's a search term */}
              {searchTerm.length >= minSearchChars ? (
                <div className="max-h-[260px] overflow-y-auto scrollbar-thin pt-1">
                  {searchResults.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {searchResults.map((item) => (
                        <DismissibleChip
                          key={item.id}
                          label={item.label}
                          onAdd={() => onAddItem(item.id)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 py-4">
                      <p className="text-center text-muted-foreground z-secondary">
                        {emptySearchMessage}
                      </p>
                      {canShowCustomAdd && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8 rounded-md gap-2"
                          disabled={customAddDisabled}
                          onClick={() => onAddCustomItem?.(trimmedSearchTerm)}
                        >
                          <Plus className="size-3.5" />
                          {customAddLabel} "{trimmedSearchTerm}"
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              ) : searchTerm.length > 0 ? (
                <p className="z-caption text-muted-foreground text-center py-2">
                  Type at least {minSearchChars} characters to search
                </p>
              ) : null}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
